import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Area, Bar, CartesianGrid, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { api } from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import {
  formatWeekInputValue,
  getIsoWeek,
  isoWeekToRange,
  parseWeekInputValue,
  shiftIsoWeek,
  sumStats,
  todayIso,
  weekdayLabel,
  type IsoWeek,
} from '../../utils/dateBuckets';
import { Card } from '../ui/Card';
import { Spinner } from '../ui/Spinner';
import { ChartTooltip, chartColors } from './ChartTooltip';
import { TrendStat } from './TrendStat';

export function WeekView() {
  const { currentSiteId } = useAuth();
  const [week, setWeek] = useState<IsoWeek>(() => getIsoWeek(todayIso()));
  const prevWeek = shiftIsoWeek(week.isoYear, week.isoWeek, -1);
  const range = isoWeekToRange(week.isoYear, week.isoWeek);
  const prevRange = isoWeekToRange(prevWeek.isoYear, prevWeek.isoWeek);

  const current = useQuery({
    queryKey: ['daily-stats', currentSiteId, range.from, range.to],
    queryFn: () => api.dailyStats(currentSiteId!, range.from, range.to),
    enabled: !!currentSiteId,
  });
  const previous = useQuery({
    queryKey: ['daily-stats', currentSiteId, prevRange.from, prevRange.to],
    queryFn: () => api.dailyStats(currentSiteId!, prevRange.from, prevRange.to),
    enabled: !!currentSiteId,
  });

  const days = current.data?.days ?? [];
  const chartData = days.map((d) => ({ ...d, label: weekdayLabel(d.date) }));
  const totals = sumStats(days);
  const prevTotals = sumStats(previous.data?.days ?? []);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <label htmlFor="week-picker" className="text-sm text-slate-500 dark:text-slate-400">
          Kalenderwoche:
        </label>
        <input
          id="week-picker"
          type="week"
          value={formatWeekInputValue(week)}
          onChange={(e) => e.target.value && setWeek(parseWeekInputValue(e.target.value))}
          className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-slate-700 dark:bg-slate-800 dark:text-white"
        />
        <span className="text-xs text-slate-400 dark:text-slate-500">
          {range.from} – {range.to}
        </span>
      </div>

      {current.isLoading ? (
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <TrendStat label="Besucher" value={totals.visitors} previousValue={prevTotals.visitors} />
            <TrendStat label="Sessions" value={totals.sessions} previousValue={prevTotals.sessions} />
            <TrendStat label="Conversions" value={totals.conversions} previousValue={prevTotals.conversions} />
            <TrendStat label="Seitenaufrufe" value={totals.pageViews} previousValue={prevTotals.pageViews} />
          </div>

          <Card title={`KW ${week.isoWeek} · ${week.isoYear}`}>
            <ResponsiveContainer width="100%" height={300}>
              <ComposedChart data={chartData}>
                <defs>
                  <linearGradient id="weekVisitors" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={chartColors.visitors} stopOpacity={0.45} />
                    <stop offset="95%" stopColor={chartColors.visitors} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
                <XAxis dataKey="label" stroke="#64748b" fontSize={12} tickLine={false} axisLine={false} />
                <YAxis stroke="#64748b" fontSize={12} allowDecimals={false} tickLine={false} axisLine={false} />
                <Tooltip content={<ChartTooltip />} />
                <Area
                  type="monotone"
                  dataKey="visitors"
                  name="Besucher"
                  stroke={chartColors.visitors}
                  strokeWidth={2}
                  fill="url(#weekVisitors)"
                />
                <Line type="monotone" dataKey="sessions" name="Sessions" stroke={chartColors.sessions} strokeWidth={2} dot={false} />
                <Bar dataKey="conversions" name="Conversions" fill={chartColors.conversions} radius={[4, 4, 0, 0]} barSize={18} />
              </ComposedChart>
            </ResponsiveContainer>
          </Card>
        </>
      )}
    </div>
  );
}
